import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { ChevronUp, ChevronDown } from "lucide-react";

interface Contact {
  id: string;
  name: string;
  email?: string;
  phone?: string;
  type: 'client' | 'prospect';
  organizationId: number;
}

interface ContactListTableProps {
  contacts: Contact[];
  selectedContacts: Set<string>;
  onToggleSelect: (contact: Contact) => void;
  onToggleSelectAll: () => void;
  sortField: 'name' | 'email' | 'phone' | 'type' | null;
  sortDirection: 'asc' | 'desc';
  onSort: (field: 'name' | 'email' | 'phone' | 'type') => void;
  contactType: 'email' | 'sms';
  paginatedContacts: Contact[];
  allSelected: boolean;
}

export function ContactListTable({
  contacts,
  selectedContacts,
  onToggleSelect,
  onToggleSelectAll,
  sortField,
  sortDirection,
  onSort,
  contactType,
  paginatedContacts,
  allSelected,
}: ContactListTableProps) {
  const getSortIcon = (field: 'name' | 'email' | 'phone' | 'type') => {
    if (sortField !== field) {
      return (
        <div className="flex flex-col w-5 h-5 text-blue-500">
          <ChevronUp className="w-5 h-3" />
          <ChevronDown className="w-5 h-3" />
        </div>
      );
    }
    return sortDirection === 'asc' ? 
      <ChevronUp className="w-5 h-5 text-blue-600" /> : 
      <ChevronDown className="w-5 h-5 text-blue-600" />;
  };

  const contactKey = (contact: Contact) => `${contact.type}-${contact.id}`;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead className="w-12">
            <Checkbox
              checked={allSelected}
              onCheckedChange={onToggleSelectAll}
              aria-label="Select all contacts"
            />
          </TableHead>
          <TableHead 
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => onSort('name')}
          >
            <div className="flex items-center justify-between">
              Name
              {getSortIcon('name')}
            </div>
          </TableHead>
          {contactType === 'email' ? (
            <TableHead 
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('email')}
            >
              <div className="flex items-center justify-between">
                Email
                {getSortIcon('email')}
              </div>
            </TableHead>
          ) : (
            <TableHead 
              className="cursor-pointer hover:bg-muted/50 select-none"
              onClick={() => onSort('phone')}
            >
              <div className="flex items-center justify-between">
                Phone
                {getSortIcon('phone')}
              </div>
            </TableHead>
          )}
          <TableHead 
            className="cursor-pointer hover:bg-muted/50 select-none"
            onClick={() => onSort('type')}
          >
            <div className="flex items-center justify-between">
              Type
              {getSortIcon('type')}
            </div>
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {paginatedContacts.map((contact, index) => (
          <TableRow key={`${contact.type}-${contact.id}-${index}`}>
            <TableCell>
              <Checkbox
                checked={selectedContacts.has(contactKey(contact))}
                onCheckedChange={() => onToggleSelect(contact)}
                aria-label={`Select ${contact.name}`}
              />
            </TableCell>
            <TableCell className="font-medium">{contact.name}</TableCell>
            <TableCell>
              {contactType === 'email' ? contact.email : contact.phone}
            </TableCell>
            <TableCell>
              <Badge variant="outline">
                {contact.type}
              </Badge>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

